import i18next from 'i18next';

import type { EpisodeMetadata, Series } from '../../types/series';
import type { PlaylistItem } from '../../types/playlist';
import type { SerializedWatchHistoryItem } from '../../types/watchHistory';
import { VideoProgressMinMax } from '../constants';

/**
 * Get an array of options for a season filter
 */
export const getFiltersFromSeries = (series: Series | undefined): { label: string; value: string }[] => {
  return series && 'seasons' in series
    ? series.seasons.map((season) => ({
        label: season.season_title || i18next.t('video:season_number_filter_template', { seasonNumber: String(season.season_number) }),
        value: String(season.season_number),
      }))
    : [];
};

/** Get a total amount of episodes in a season */
export const getEpisodesInSeason = (episodeMetadata: EpisodeMetadata | undefined, series: Series | undefined) => {
  return (series?.seasons || []).find((el) => el.season_number === Number(episodeMetadata?.seasonNumber))?.episode_count;
};

export const resolveNextEpisode = ({ episodeId, episodes }: { episodeId: string; episodes: PlaylistItem[] }) => {
  if (!episodes.length) return;

  const episodeIndex = episodes.findIndex((episode) => episode.mediaid === episodeId);

  return episodeIndex > -1 ? episodes[episodeIndex + 1] : undefined;
};

/**
 * Return the current and next episode for the
 * @param episodeId
 * @param episodes
 * @param watchHistoryItem
 */
export const resolveEpisode = ({
  episodeId,
  episodes,
  watchHistoryItem,
}: {
  episodeId?: string;
  episodes: PlaylistItem[];
  watchHistoryItem: SerializedWatchHistoryItem | undefined;
}): { episode: PlaylistItem; firstEpisode: PlaylistItem; nextEpisode: PlaylistItem | undefined; showEpisodeInfo: boolean } => {
  if (!episodes.length) throw new Error('No episodes available');

  const firstEpisode = episodes[0];

  // Get episode if the episode id is given (deeplink, clicked episode, etc)
  if (episodeId) {
    const episode = episodes.find((episode) => episode.mediaid === episodeId);
    if (episode) {
      return { episode, nextEpisode: resolveNextEpisode({ episodeId, episodes }), firstEpisode, showEpisodeInfo: true };
    }
  }

  // Get episode from the current watch history if the user is watching this series
  if (watchHistoryItem) {
    const episode =
      watchHistoryItem.progress >= VideoProgressMinMax.Max
        ? resolveNextEpisode({ episodes, episodeId: watchHistoryItem.mediaid })
        : episodes.find((episode) => episode.mediaid === watchHistoryItem.mediaid);

    if (episode) {
      return { episode, nextEpisode: resolveNextEpisode({ episodes, episodeId: episode.mediaid }), firstEpisode, showEpisodeInfo: true };
    }
  }

  // Fallback to the first episode
  return {
    episode: firstEpisode,
    nextEpisode: resolveNextEpisode({ episodes, episodeId: firstEpisode.mediaid }),
    firstEpisode,
    showEpisodeInfo: false,
  };
};
